/**
 * JSON event conversion - clean and simple
 */
import {Event} from '../../event';
import {EventType} from '../../eventType';
import {DOMUtils} from '../../utils/dom';
import {generateGovolteEventId} from './govolteEventIdGenerator';

export interface JsonEvent {
  event_type: string;
  client_timestamp_iso?: string;
  error_code?: string;
  event_id: string;
  is_new_party: boolean;
  is_new_session: boolean;
  parameters?: JsonEventParameters;
  party_id: string;
  sdk_version?: string;
  session_id: string;
}

export interface JsonEventParameters {
  error?: ErrorParameters;
  click?: ClickParameters;
  scroll?: ScrollParameters;
  condition?: string;
  experiment?: string;
  customLabels?: CustomLabels;
  location?: string;
  npo?: NpoParameters;
  recommendation?: RecommendationParameters;
  referrer?: string;
  screen?: ScreenParameters;
  stream?: StreamParameters;
  topspin?: TopspinParameters;
}

export interface TopspinParameters {
  avType?: string;
  avSubtype?: string;
  brand?: string;
  brandId?: number;
  environment?: string;
  broadcaster?: string;
  chapters?: ChaptersParameters;
  content_context_id?: string;
  nmoid?: string;
  pageName?: string;
  pageId?: string;
  scrollEnabled?: boolean;
  platformType?: string;
  platformVersion?: string;
  program?: string;
}

export interface ChaptersParameters {
  chapter1?: string;
  chapter2?: string;
  chapter3?: string;
}

export interface NpoParameters {
  profileId?: string;
  pseudoId?: string;
  subscription?: string;
  userId?: string;
  userProfileType?: string;
}

export interface CustomLabels {
  label1?: string;
  label2?: string;
  label3?: string;
  label4?: string;
  label5?: string;
}

export interface ScreenParameters {
  height?: number;
  orientation?: string;
  pixelRatio?: number;
  width?: number;
}

export interface StreamParameters {
  id?: string;
  length?: number; // seconds
  adLength?: number; // seconds
  liveOffset?: number; // seconds
  liveOffsetFrom?: number; // seconds
  isLiveStream?: boolean;
  playerId?: string;
  playerVersion?: string;
  skoPlayerVersion?: string;
  position?: number; // seconds
  seekFrom?: number; // seconds
  bitrateAudio?: string;
  bitrateVideo?: string;
  startupTime?: number;
  drmLoadTime?: number;
  errorCode?: number;
  errorDescription?: string;
  isp?: string;
  mediaUrl?: string;
  drmType?: string;
}

export interface RecommendationParameters {
  destinations?: DestinationsParameters[];
  panel?: string;
  panel_format?: string;
  position?: number;
  offerId?: string;
  query?: string;
}

export interface DestinationsParameters {
  contentId: string;
  index: number;
  numberDisplayed: number;
  recommender: string;
}

export interface ClickParameters {
  chapter_1?: string;
  chapter_2?: string;
  chapter_3?: string;
  name?: string;
  position_x?: number;
  position_y?: number;
  type?: string;
}

export interface ErrorParameters {
  message: string;
}

export interface ScrollParameters {
  percentage: number;
}

const domUtils = DOMUtils();

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const numberValue = Number(trimmedValue);
  return isFinite(numberValue) ? numberValue : undefined;
};

export const eventToJsonEvent = (
  eventType: EventType,
  event: Event
): JsonEvent => {
  // For event format: https://github.com/Nederlandse-Publieke-Omroep/npo-datahub-govolte/blob/staging/internal/govolte-schemas/web_json_schema_v1.go

  const jsonEvent: JsonEvent = {
    event_type: eventType,
    client_timestamp_iso: event.client_timestamp,
    event_id: generateGovolteEventId(event.pageViewId),
    is_new_party: event.isNewParty,
    is_new_session: event.isFirstInSession,
    party_id: event.party_id,
    session_id: event.session_id,
    sdk_version: event.sdk_version,
  };

  // Add error code if present
  if (event.error_code) {
    jsonEvent.error_code = event.error_code;
  }

  // Build parameters - only what we need
  const parameters: Record<string, any> = {};
  const query = event.query || event.query_context;

  // Topspin
  if (
    event.brand ||
    event.brand_id ||
    event.nmoid ||
    event.page ||
    event.pageViewId
  ) {
    parameters.topspin = {
      brand: event.brand,
      brandId: event.brand_id,
      nmoid: event.nmoid,
      pageName: event.page,
      pageId: event.pageViewId,
      content_context_id: event.content_context_id,
      platformType: event.platform,
      platformVersion: event.platform_version,
      program: event.program,
      broadcaster: event.broadcasters,
      environment: event.environment,
      scrollEnabled: event.scroll_tracking,
      avType: event.av_type,
      avSubtype: event.av_subtype,
    };

    if (event.chapter_1 || event.chapter_2 || event.chapter_3) {
      parameters.topspin.chapters = {
        chapter1: event.chapter_1,
        chapter2: event.chapter_2,
        chapter3: event.chapter_3,
      };
    }
  }

  // NPO
  if (event.user_id || event.user_profile || event.user_subscription) {
    parameters.npo = {
      userId: event.user_id,
      profileId: event.user_profile,
      subscription: event.user_subscription,
      pseudoId: event.user_pseudoid,
      userProfileType: event.user_profile_type,
    };
  }

  // Stream
  if (event.stream_id || event.stream_position !== undefined) {
    parameters.stream = {
      id: event.stream_id,
      length: event.stream_length,
      adLength: event.stream_ad_length,
      position: event.stream_position,
      playerId: event.player_id,
      playerVersion: event.player_version,
      skoPlayerVersion: event.sko_player_version,
      liveOffset: event.live_offset,
      liveOffsetFrom: event.live_offset_from,
      isLiveStream:
        event.live_offset !== undefined && event.live_offset !== null,
      seekFrom: event.stream_seek_from,
      bitrateAudio: event.bitrate_audio,
      bitrateVideo: event.bitrate_video,
      startupTime: event.startupTime,
      drmLoadTime: event.drmLoadTime,
      errorCode: event.stream_error_code,
      errorDescription: event.stream_error_description,
      isp: event.isp,
      mediaUrl: event.mediaURL,
      drmType: event.drmType,
    };
  }

  // Recommendation
  if (
    event.panel_id ||
    event.panel_format ||
    event.panel_position !== undefined ||
    event.offer_id ||
    query ||
    event.recommender ||
    event.target_id ||
    event.offer_index !== undefined ||
    event.total_offers !== undefined
  ) {
    const panelPosition = toFiniteNumber(event.panel_position);
    const offerIndex = toFiniteNumber(event.offer_index);
    const totalOffers = toFiniteNumber(event.total_offers);

    parameters.recommendation = {
      panel: event.panel_id,
      panel_format: event.panel_format,
      position: panelPosition,
      offerId: event.offer_id,
      query,
    };
    if (
      event.recommender &&
      event.target_id &&
      offerIndex !== undefined &&
      totalOffers !== undefined
    ) {
      parameters.recommendation.destinations = [
        {
          recommender: event.recommender,
          contentId: event.target_id,
          index: offerIndex,
          numberDisplayed: totalOffers,
        },
      ];
    }
  }

  if (event.experiment) {
    parameters.experiment = event.experiment;
  }

  // Screen
  parameters.screen = {
    height: domUtils.getWindowHeight(),
    width: domUtils.getWindowWidth(),
    orientation: domUtils.getScreenOrientation(),
    pixelRatio: window.devicePixelRatio,
  };

  // Click
  if (event.click_name || event.click_type) {
    parameters.click = {
      name: event.click_name,
      type: event.click_type,
      position_x: event.click_position_x,
      position_y: event.click_position_y,
      chapter_1: event.click_chapter_1,
      chapter_2: event.click_chapter_2,
      chapter_3: event.click_chapter_3,
    };
  }

  // Error
  if (event.error_message) {
    parameters.error = {message: event.error_message};
  }

  // Scroll
  if (event.scroll_percentage !== undefined) {
    parameters.scroll = {percentage: event.scroll_percentage};
  }

  if (Object.keys(parameters).length > 0) {
    jsonEvent.parameters = parameters;
  }

  return jsonEvent;
};

export const jsonEventToShortenedFormat = (
  jsonEvent: JsonEvent,
  event: Event
): Record<string, any> => {
  const domUtils = DOMUtils();

  const shortened: Record<string, any> = {
    t: jsonEvent.event_type,
    c: jsonEvent.client_timestamp_iso,
    e: jsonEvent.event_id,
    n: jsonEvent.is_new_party,
    f: jsonEvent.is_new_session,
    p: jsonEvent.party_id,
    s: jsonEvent.session_id,
  };

  // Add optional fields with shortened names where specified
  if (event.condition) {
    shortened.condition = event.condition;
  }
  if (jsonEvent.error_code) {
    shortened.error_code = jsonEvent.error_code;
  }
  if (event.location) {
    shortened.l = event.location;
  }
  if (event.referrer) {
    shortened.r = event.referrer;
  }

  // Screen properties with shortened names
  shortened.j = domUtils.getScreenHeight();
  shortened.i = domUtils.getScreenWidth();
  shortened.k = window.devicePixelRatio;
  shortened.screen_orientation = domUtils.getScreenOrientation();
  if (jsonEvent.sdk_version) {
    shortened.sdk_version = jsonEvent.sdk_version;
  }

  // Custom labels (not shortened)
  const labels = [
    event.custom_label1,
    event.custom_label2,
    event.custom_label3,
    event.custom_label4,
    event.custom_label5,
  ];
  if (labels.some(Boolean)) {
    shortened.customLabels = {
      label1: event.custom_label1,
      label2: event.custom_label2,
      label3: event.custom_label3,
      label4: event.custom_label4,
      label5: event.custom_label5,
    };
  }

  // Add parameters if present
  if (jsonEvent.parameters) {
    shortened.parameters = jsonEvent.parameters;
  }

  return shortened;
};
